import React from 'react';
import { Col, Typography } from 'antd';
import {
  TeamOutlined,
  IdcardOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ExceptionOutlined,
  UserAddOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import type { HrmsDashboardActionSummary } from '@/services/hrmsDashboard.service';

const { Text } = Typography;

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const HrmsOutlookRibbon: React.FC<{
  summary: HrmsDashboardActionSummary;
}> = ({ summary }) => {
  const navigate = useNavigate();
  const t = summary.talent;

  return (
    <div id="outlook">
      <ControlTowerMetricStrip zone="metricsOrders" title="Outlook (forward look)">
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Active headcount"
            count={summary.active_headcount}
            icon={<TeamOutlined />}
            urgency="neutral"
            extra={
              summary.on_leave_today_count > 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {summary.on_leave_today_count} on leave today
                </Text>
              ) : undefined
            }
            footerLink={{
              label: 'Employees',
              onClick: () => navigate('/hr/employees'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Docs due in 30d"
            count={summary.docs_due_soon_count}
            icon={<IdcardOutlined />}
            urgency={
              summary.expired_docs_count > 0
                ? 'critical'
                : summary.docs_due_soon_count > 0
                  ? 'attention'
                  : 'neutral'
            }
            extra={
              summary.expired_docs_count > 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {summary.expired_docs_count} already expired
                </Text>
              ) : undefined
            }
            footerLink={{
              label: 'Follow up',
              onClick: () => scrollTo('hrms-follow-up'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Contracts ending 30d"
            count={summary.contracts_ending_count}
            icon={<SolutionOutlined />}
            urgency={summary.contracts_ending_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'Queue',
              onClick: () => scrollTo('hrms-follow-up'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Joiners next 14d"
            count={summary.joiners_next_14d_count}
            icon={<UserAddOutlined />}
            urgency={summary.joiners_next_14d_count > 0 ? 'attention' : 'neutral'}
            extra={
              summary.joiners_this_week_count + summary.leavers_this_week_count > 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Week: +{summary.joiners_this_week_count} / −{summary.leavers_this_week_count}
                </Text>
              ) : undefined
            }
            footerLink={{
              label: 'Employees',
              onClick: () => navigate('/hr/employees'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Leave pending"
            count={summary.pending_leave_count}
            icon={<ClockCircleOutlined />}
            urgency={summary.pending_leave_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'Queue',
              onClick: () => scrollTo('hrms-follow-up'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Confirmation due"
            count={summary.confirmation_due_count}
            icon={<ExceptionOutlined />}
            urgency={summary.confirmation_due_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'Follow up',
              onClick: () => scrollTo('hrms-follow-up'),
            }}
          />
        </Col>
        {t.enabled ? (
          <>
            <Col xs={24} sm={12} md={8} lg={6}>
              <ControlTowerMetricTile
                title="Interviews next 7d"
                count={t.interviews_next_7d}
                icon={<ClockCircleOutlined />}
                urgency={t.interviews_next_7d > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Talent',
                  onClick: () => scrollTo('talent-snapshot'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <ControlTowerMetricTile
                title="Offers out"
                count={t.offers_awaiting_response}
                icon={<FileTextOutlined />}
                urgency={t.offers_awaiting_response > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Recruitment',
                  onClick: () => navigate('/hr/recruitment'),
                }}
              />
            </Col>
          </>
        ) : null}
        {summary.payroll_enabled ? (
          <Col xs={24} sm={12} md={8} lg={6}>
            <ControlTowerMetricTile
              title="Payroll period"
              count={0}
              valueDisplay={summary.payroll_period_status || '—'}
              icon={<FileTextOutlined />}
              urgency={
                summary.payroll_period_status &&
                !['Closed', 'Paid', 'Approved'].includes(summary.payroll_period_status)
                  ? 'attention'
                  : 'neutral'
              }
              extra={
                summary.payroll_period_label ? (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {summary.payroll_period_label}
                  </Text>
                ) : undefined
              }
              footerLink={{
                label: 'Payroll',
                onClick: () => scrollTo('payroll-readiness'),
              }}
            />
          </Col>
        ) : null}
      </ControlTowerMetricStrip>
    </div>
  );
};

export default HrmsOutlookRibbon;
